import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Button,
  MenuItem,
  Box
} from "@mui/material";

import { isEmailValid } from "../../utils";

const INITIAL_VALUES = {
  fullName: "",
  email: "",
  role: "",
  password: "",
  confirmPassword: "",
};

export default function UserDialog({ open, onClose, onSave, rowId, user }) {
  const [data, setData] = useState(INITIAL_VALUES);
  const [emailError, setEmailError] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (rowId && user) {
      setData({
        fullName: user.fullName || "",
        email: user.email || "",
        role: user.role || "",
        password: "",
        confirmPassword: "",
      });
    } else {
      setData(INITIAL_VALUES);
    }
  }, [rowId, user, open]);



  const handleClose = () => {
    setData(INITIAL_VALUES);
    onClose();
  };

  const isValid = () => {
    if (!data.fullName || !data.email || !data.role) return false;

    if (!isEmailValid(data.email)) return false;

    if (!rowId) {
      if (!data.password || !data.confirmPassword) return false;
      if (data.password !== data.confirmPassword) return false;
      if (data.password.length < 8) return false; 
    }

    return true;
  };

 const handleSave = () => {
  const payload = {
    fullName: data.fullName,
    email: data.email,
    role: data.role,
  };

  if (!rowId) {
    payload.password = data.password;
    payload.confirmPassword = data.confirmPassword;
  }

  onSave(payload);
  handleClose();
};

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="md"
    >
      <DialogTitle>{rowId ? "Edit User" : "Create User"}</DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          {rowId
            ? "Update the user's information."
            : "Fill out the information to create a new user."}
        </DialogContentText>

        <Box
          component="form"
          autoComplete="off"
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >

          <Box sx={{ display: "flex", gap: 3 }}>
            <TextField
              label="Full Name"
              fullWidth
              required
              value={data.fullName}
              onChange={(e) => setData({ ...data, fullName: e.target.value })}
              helperText="This field is required"
            />
            <TextField
              label="Email"
              type="email"
              name="email"
              autoComplete="off"
              fullWidth
              required
              value={data.email}
              onChange={(e) => {
                const value = e.target.value;
                setData({ ...data, email: value });
                setEmailError(value && !isEmailValid(value));
              }}
              error={emailError}
              helperText={
                emailError
                  ? "Enter a valid email"
                  : "This field is required"
              }
            />
          </Box>
          
          {!rowId && (
            <Box sx={{ display: "flex", gap: 3 }}>
              <TextField
                label="Password"
                type="password"
                name="password"
                autoComplete={rowId ? "off" : "new-password"}
                fullWidth
                required
                value={data.password}
                onChange={(e) => setData({ ...data, password: e.target.value })}
                error={data.password.length > 0 && data.password.length < 8}
                helperText="Minimum 8 characters"
              />

              <TextField
                label="Confirm Password"
                type="password"
                name="confirmPassword"
                autoComplete="new-password"
                fullWidth
                required
                value={data.confirmPassword}
                onChange={(e) =>
                  setData({ ...data, confirmPassword: e.target.value })
                }
                error={
                  (data.confirmPassword.length > 0 && data.confirmPassword.length < 8) ||
                  (data.confirmPassword.length >= 8 && data.password !== data.confirmPassword)
                }
                helperText={
                  !data.confirmPassword
                    ? "Minimum 8 characters"
                    : data.confirmPassword.length < 8
                    ? "Minimum 8 characters"
                    : data.password !== data.confirmPassword
                    ? "Passwords do not match"
                    : "Minimum 8 characters"
                }
              />
            </Box>
          )}

          <TextField
            select
            label="Role"
            fullWidth
            required
            value={data.role}
            onChange={(e) => setData({ ...data, role: e.target.value })}
            helperText="This field is required"
          >
            <MenuItem value="admin">ADMINISTRATOR</MenuItem>
            <MenuItem value="supervisor">SUPERVISOR</MenuItem>
            <MenuItem value="warehouse">WAREHOUSE</MenuItem>
            <MenuItem value="retail">RETAIL & CONTENT</MenuItem>
            <MenuItem value="events">EVENTS MANAGER</MenuItem>
          </TextField>

        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={!isValid()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}